import React, { useContext, useState, useEffect } from "react"
import _ from "lodash"
import { Link } from "react-router-dom"
import { useLazyQuery, useMutation } from "@apollo/client"

import { InputSwitch } from "primereact/inputswitch"
import PageHeader from "../../components/PageHeader"
import { LoaderMedium } from "../../components/Loaders"
import { ElementLoading } from "../../components/ElementLoading"
import GenericTable from "../../components/DataTable/GenericTable"
import FilterHeader from "../../components/DataTable/FilterHeader"
import { useFilters } from "../../components/Filters/useFilters"
import { UserContext } from "../../context/userContext"
import OrganizationMenuSidebar from "../../components/sidebars/OrganizationMenuSidebar"
import PageSidebarContent from "../../components/sidebars/PageSidebarContent"
import { CHANGE_STATUS_PROGRAM, GET_PROGRAMS_BY_CTX } from "./queries"

export default function ProgramsListOrg() {
  const userCtx = useContext(UserContext)
  const [programs, setPrograms] = useState([])
  const [loadingPage, setLoadingPage] = useState(true)
  const [changeStatusProgram] = useMutation(CHANGE_STATUS_PROGRAM)
  const [organizations, setOrganizations] = useState([])
  const [loadingStatus, setLoadingStatus] = useState({})

  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    onFilterChange,
    clearGlobalFilter,
    clearAllFilters,
  } = useFilters()

  const [getPrograms, { loading, data }] = useLazyQuery(GET_PROGRAMS_BY_CTX, {
    variables: {
      staff_id: userCtx.session.user?.staff_id,
      organization_id: userCtx.session.user?.organization_id,
    },
    skip: !userCtx.session.user.staff_id,
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      const uniqueOrganizations = _.uniq(
        data.programs.map((program) => program.organization_name)
      ).map((org) => ({ label: org, value: org }))

      setOrganizations([{ label: "All", value: "" }, ...uniqueOrganizations])

      setPrograms(
        _.map(data.programs, (program) => {
          return {
            ...program,
            organization_name: _.unescape(program.organization_name),
            status: program.program_id_enterprise_managed ? (
              `${_.upperFirst(_.toLower(String(program.program_id_status)))}`
            ) : (
              <ElementLoading
                status={program.program_id_status}
                item={program.program_identity_id}
                parentCall={(value) =>
                  handleToggleChange(value, program.program_identity_id)
                }
                organization_id={program.organization_id}
              >
                <InputSwitch
                  checked={program.program_id_status === "ACTIVE"}
                  disabled={loadingStatus[program.program_identity_id]}
                />
              </ElementLoading>
            ),
            edit: program.program_id_enterprise_managed ? null : (
              <Link
                to={`/organization/form/program/1/${program.program_identity_id}`}
                className="btn"
              >
                Edit
                <i className="pi pi-pencil"></i>
              </Link>
            ),
          }
        })
      )
      setLoadingPage(false)
    },
  })

  const handleToggleChange = async (value, program_identity_id) => {
    setLoadingStatus((prev) => ({ ...prev, [program_identity_id]: true }))
    try {
      await changeStatusProgram({
        variables: {
          staff_id: userCtx.session.user.staff_id,
          staff_alias: userCtx.session.user.staff_alias,
          program_identity_id,
          program_id_status: value === true ? "ACTIVE" : "INACTIVE",
        },
        skip: !userCtx.session.user.staff_id,
        fetchPolicy: "no-cache",
        onCompleted: async () => {
          getPrograms()
        },
      })
    } catch (error) {
      console.error("Error changing status:", error)
    } finally {
      setLoadingStatus((prev) => ({ ...prev, [program_identity_id]: false }))
    }
  }

  useEffect(() => {
    getPrograms()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filterConfig = [
    {
      label: "GPRA",
      value: filters.program_id_gpra?.value || false,
      options: [
        { label: "All", value: "" },
        { label: "Active", value: "true" },
        { label: "Inactive", value: "false" },
      ],
      onChange: (e) => onFilterChange("program_id_gpra", e.value),
      onClear: () => onFilterChange("program_id_gpra", null),
      placeholder: "Select GPRA",
    },
    {
      label: "Status",
      value: filters.program_id_status?.value || "",
      options: [
        { label: "All", value: "" },
        { label: "Active", value: "ACTIVE" },
        { label: "Inactive", value: "INACTIVE" },
      ],
      onChange: (e) => onFilterChange("program_id_status", e.value),
      onClear: () => onFilterChange("program_id_status", null),
      placeholder: "Select Status",
    },
  ]

  const columns = [
    { field: "program_id_name", header: "Program Name", sortable: true },
    {
      field: "program_id_enterprise_managed",
      header: "Enterprise Managed",
      body: (rowData) => (rowData.program_id_enterprise_managed ? "Yes" : ""),
      sortable: true,
    },
    {
      field: "program_id_code",
      header: "Billing or service code",
      sortable: true,
    },
    {
      field: "program_id_gpra",
      header: "GPRA",
      body: (rowData) => (rowData.program_id_gpra ? "Active" : "Inactive"),
      sortable: true,
    },
    { field: "status", header: "Status", sortable: true },
    { field: "edit", header: "Edit", sortable: true },
  ]

  const sidebarContent = <OrganizationMenuSidebar />

  const setToLoading = loadingPage || userCtx.globalLoading

  if (setToLoading)
    return (
      <>
        <PageSidebarContent>{sidebarContent}</PageSidebarContent>
        <LoaderMedium />
      </>
    )

  return (
    <>
      <PageSidebarContent>{sidebarContent}</PageSidebarContent>
      <div className="main-content">
        <PageHeader
          title="Manage Program ID"
          actions={[
            {
              to: "/organization/form/program/1/",
              label: "Create new Program ID",
            },
          ]}
        />

        <GenericTable
          data={programs}
          columns={columns}
          filters={filters}
          header={
            <FilterHeader
              filters={filterConfig}
              globalFilterValue={globalFilterValue}
              onGlobalFilterChange={onGlobalFilterChange}
              clearGlobalFilter={clearGlobalFilter}
              clearAllFilters={clearAllFilters}
            />
          }
          paginator
          rows={50}
          sortField="program_id_name"
          sortOrder={1}
        />
      </div>
    </>
  )
}
